import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, CreditCard, Receipt } from 'lucide-react';
import {
  ApiError,
  cancelSubscription,
  createCheckout,
  getPlans,
  getSubscription,
  qk,
  type Invoice,
  type Plan,
} from '../api.js';
import BilgiDugmesi from '../components/BilgiDugmesi.js';
import Badge, { type BadgeTone } from '../components/Badge.js';
import Card from '../components/Card.js';
import { ErrorState } from '../components/EmptyState.js';
import Table from '../components/Table.js';
import { useOrgContext } from '../components/Layout.js';
import { orgRoleAtLeast } from '../lib/auth.js';
import { formatDate, formatNumber, TL } from '../lib/format.js';

/**
 * §45 — Faturalama sayfası: plan kataloğu · abonelik durumu · fatura geçmişi.
 *
 * SAYFA ORG DÜZEYİNDEDİR (proje değil): plan, kota ve fatura §42/§44/§45 boyunca
 * org'a asılıdır — bu yüzden aktif projeyi değil `useOrgContext()`i okur.
 *
 * SAYFANIN TUTTUĞU KURAL: panel abonelik durumunu KENDİ İLERLETMEZ. Checkout yanıtı
 * bir söz değildir (§45.1 — durum yalnız webhook'tan gelir), iptal de dönem SONUNDA
 * yürürlüğe girer (§45.3). Bu yüzden hiçbir düğme "planınız yükseldi/abonelik bitti"
 * demez; ekran her zaman sunucudan OKUNAN durumu gösterir.
 *
 * Görsel dil Ayarlar/SEO sayfalarıyla birebir: beyaz başlık şeridi → beyaz istatistik
 * şeridi → #fafafa zeminde px-6 py-8 içerik.
 */

/** §45 panel yolu — `MockCheckout` ödeme sonrası buraya döner, Layout rayı buna bağlar. */
export const BILLING_PATH = '/faturalama';

/**
 * §45.3 — aboneliği olmayan / iptal edilen org'un düştüğü plan. Değer sunucudaki
 * `FREE_PLAN_ID` ve `014_plans.sql` seed'iyle AYNI olmak zorundadır: ayrışırsa panel
 * "mevcut plan" işaretini hiçbir karta koyamaz.
 */
const UCRETSIZ_PLAN_ID = 'free';

/**
 * §45.2/§45.3 abonelik durumu → rozet. Renk TEK BAŞINA taşıyıcı değildir; metin de
 * durumu söyler (erişilebilirlik, §26.6 rozetleriyle aynı karar).
 *
 * Anahtar `string`tir: sunucu ileride yeni bir duruma geçerse tanınmayan değer ham
 * hâliyle gösterilir — panel `undefined` çizip durumu SAKLAMAZ.
 */
const ABONELIK_DURUMU: Record<string, { label: string; tone: BadgeTone; title: string }> = {
  active: {
    label: 'aktif',
    tone: 'success',
    title: 'Ödeme alındı; plan yürürlükte.',
  },
  past_due: {
    label: 'ödeme gecikti',
    tone: 'warning',
    // §45.3 YUMUŞAK YAPTIRIM — kullanıcı "hesabım kapandı mı?" diye sormamalı.
    title: 'Tahsilat başarısız oldu. Planınız DEĞİŞMEDİ ve özellikler kısılmadı; ödeme yöntemini güncelleyin.',
  },
  canceled: {
    label: 'iptal edildi',
    tone: 'danger',
    title: 'Abonelik sona erdi; organizasyon ücretsiz plana düştü. Veriler SİLİNMEZ (§45.3).',
  },
};

/** §45.2 fatura durumu — sağlayıcının aynası; biz fatura üretmeyiz. */
const FATURA_DURUMU: Record<string, { label: string; tone: BadgeTone }> = {
  paid: { label: 'ödendi', tone: 'success' },
  open: { label: 'açık', tone: 'neutral' },
  void: { label: 'iptal', tone: 'outline' },
};

/**
 * Tanınmayan durum HAM HÂLİYLE gösterilir.
 *
 * NEDEN: sözlük sunucununkinin AYNASIDIR ve sağlayıcı katalogları büyür. Bilinmeyen
 * bir değeri boş bırakmak, kullanıcıya "durum yok" yalanını söylerdi; ham değer en
 * azından destek konuşmasında aranabilir.
 */
function abonelikRozeti(status: string): { label: string; tone: BadgeTone; title: string } {
  return (
    ABONELIK_DURUMU[status] ?? {
      label: status,
      tone: 'neutral',
      title: 'Bu panel sürümünün tanımadığı bir abonelik durumu.',
    }
  );
}

function faturaRozeti(status: string): { label: string; tone: BadgeTone } {
  return FATURA_DURUMU[status] ?? { label: status, tone: 'neutral' };
}

/** §44.1 `plans.features` anahtarları — panelde Türkçe etiketle çizilir. */
const OZELLIK_ETIKETLERI: Record<string, string> = {
  sso: 'SSO (§46)',
  webhooks: 'Webhook (§47)',
};

/** Yalnız AÇIK özellikler rozetlenir; kapalı özelliği listelemek gürültüdür. */
function planOzellikleri(plan: Plan): string[] {
  return Object.entries(plan.features)
    .filter(([, value]) => value === true)
    .map(([key]) => OZELLIK_ETIKETLERI[key] ?? key);
}

/**
 * §44.1/§45.2 — para KURUŞ cinsinden TAM SAYIDIR (float para YASAK), gösterimde 100'e
 * bölünür. Kuruş kısmı sıfırsa yazılmaz ('999 ₺'), değilse gösterilir ('99,9 ₺').
 *
 * TRY dışı bir para birimi gelirse '₺' YERİNE kodun kendisi yazılır: her tutara lira
 * sembolü basmak, dolar cinsi bir faturayı lira gibi göstermek olurdu.
 */
export function formatKurus(kurus: number, currency: string): string {
  const tam = Math.round(kurus) / 100;
  const basamak = Math.round(kurus) % 100 === 0 ? 0 : 2;
  const kod = currency.trim().toUpperCase();
  if (kod === 'TRY' || kod === '') return `${formatNumber(tam, basamak)} ${TL}`;
  return `${formatNumber(tam, basamak)} ${kod}`;
}

/**
 * §45 hata sözleşmesi DURUM KODU + `ApiError.code`'dan okunur (mesaj metninden değil).
 *
 * İki kod kullanıcıya BAŞKA BİR EYLEM söyletir ve bu yüzden özel cümleyi hak eder:
 * `free_plan` (yapılacak iş checkout değil İPTAL) ve `no_subscription` (iptal edilecek
 * bir şey yok). Gerisinde sunucunun kendi Türkçe mesajı zaten açıklayıcıdır.
 */
function faturaHataMetni(error: unknown): string | null {
  if (error === null || error === undefined) return null;
  if (error instanceof ApiError) {
    if (error.code === 'free_plan') {
      return 'Ücretsiz plana geçmek bir ödeme değil, İPTALDİR — aboneliğinizi iptal edin; dönem sonunda ücretsiz plana inersiniz.';
    }
    if (error.code === 'no_subscription') return 'İptal edilecek etkin bir abonelik yok.';
    if (error.status === 403) {
      return 'Plan değiştirmek için organizasyon yöneticisi (admin) olmalısınız.';
    }
    if (error.status === 0) return 'Sunucuya ulaşılamadı — bağlantınızı kontrol edin.';
    return error.message;
  }
  return error instanceof Error ? error.message : String(error);
}

export default function Billing() {
  const queryClient = useQueryClient();
  const { orgId, orgs, orgRole } = useOrgContext();

  /*
   * §45.1/§45.3 — checkout ve iptal uçları org:admin ister (RBAC tablosu). Buradaki
   * kontrol GÜVENLİK DEĞİL (sunucu kararı tekrar verir), kesin 403 alacak bir düğmeyi
   * tıklatmama nezaketidir — `lib/auth.ts`'teki saf `orgRoleAtLeast` ile aynı karar.
   */
  const yetkili = orgRoleAtLeast(orgRole, 'admin');
  const orgAdi = orgs.find((o) => o.id === orgId)?.name ?? null;

  const plans = useQuery({
    queryKey: qk.plans(),
    queryFn: ({ signal }) => getPlans(signal),
    retry: false,
  });

  const billing = useQuery({
    queryKey: qk.orgSubscription(orgId ?? ''),
    queryFn: ({ signal }) => getSubscription(orgId as string, signal),
    // Org seçilmeden bu uç ÇAĞRILAMAZ (yol org kimliğini taşır); boş id 404 olurdu.
    enabled: orgId !== null,
    retry: false,
  });

  const checkout = useMutation({
    mutationFn: (planId: string) => createCheckout(orgId as string, planId),
    onSuccess: (data) => {
      /*
       * Ödeme sayfası panelin DIŞINDA olabilir (gerçek sağlayıcıda mutlak URL) — bu
       * yüzden router ile değil adres çubuğuyla gidilir. Mock'ta URL panelin kendi
       * `/odeme-simulasyonu` yoludur (§45.1) ve aynı satır onu da açar.
       */
      window.location.assign(data.url);
    },
  });

  const cancel = useMutation({
    mutationFn: () => cancelSubscription(orgId as string),
    onSuccess: () => {
      // Durum İPTALLE DEĞİŞMEZ (§45.1 — webhook yazar); yine de dönem sonu bilgisi
      // tazelensin diye sorgu geçersizlenir.
      void queryClient.invalidateQueries({ queryKey: qk.orgSubscription(orgId ?? '') });
    },
  });

  const veri = billing.data;
  const subscription = veri?.subscription ?? null;
  const invoices: Invoice[] = veri?.invoices ?? [];

  /*
   * "Mevcut plan" YALNIZ veri geldikten sonra işaretlenir (`null` = henüz bilinmiyor).
   * Yüklenirken 'free'e düşseydik panel, ödeme yapmış bir org'a bir an için "ücretsiz
   * plandasınız" derdi — sonra sessizce değişirdi; yanlış bilgi kısa süreli de olsa yanlıştır.
   */
  const mevcutPlanId =
    veri === undefined ? null : (veri.plan?.id ?? subscription?.planId ?? UCRETSIZ_PLAN_ID);
  const planListesi = plans.data?.plans ?? [];
  const mevcutPlan =
    veri?.plan ??
    (mevcutPlanId === null ? null : (planListesi.find((p) => p.id === mevcutPlanId) ?? null));

  const durum = subscription === null ? null : abonelikRozeti(subscription.status);

  /*
   * Faturalama ucu olmayan bir sunucuda (Faz 4 öncesi dağıtım) 404 gelir. Bu bir HATA
   * DEĞİL "özellik yok"tur; Ayarlar'daki üye listesiyle aynı karar — kırmızı kutu
   * yanlış alarm olurdu.
   */
  const ucYok = billing.error instanceof ApiError && billing.error.status === 404;
  const billingHata = billing.isError && !ucYok ? (billing.error as Error).message : null;
  const islemHata = faturaHataMetni(checkout.error) ?? faturaHataMetni(cancel.error);

  return (
    <div className="min-h-full bg-[#fafafa]" data-testid="faturalama-sayfasi">
      {/* başlık */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#e5e7eb] bg-white px-6 py-4">
        <div className="flex items-center gap-2">
          <h1 className="font-grotesk text-[18px] font-bold text-[#111827]">Faturalama</h1>
          <BilgiDugmesi testId="faturalama-bilgi">Plan, kota ve faturalar ORGANİZASYONA asılıdır (§44/§45), projeye değil. Abonelik durumu yalnız ödeme sağlayıcısının webhook'uyla değişir; panel onu okur, kendi ilerletmez.</BilgiDugmesi>
          <span className="ml-1 hidden text-[13px] text-[#9ca3af] md:inline">
            {orgAdi === null ? 'Organizasyon planı' : `${orgAdi} · plan ve abonelik`} (§45)
          </span>
        </div>
      </div>

      {/* istatistikler */}
      <div className="flex flex-wrap items-center gap-8 border-b border-[#e5e7eb] bg-white px-6 py-2.5">
        {billing.isLoading ? (
          <span className="animate-pulse text-[12px] text-[#9ca3af]">Veri yükleniyor…</span>
        ) : (
          [
            { label: 'Plan', value: mevcutPlan?.name ?? '—' },
            {
              label: 'Aylık olay limiti',
              value: mevcutPlan === null ? '—' : formatNumber(mevcutPlan.monthlyEventLimit),
            },
            {
              label: 'Abonelik',
              value: durum?.label ?? (veri === undefined ? '—' : 'yok (ücretsiz)'),
            },
            { label: 'Fatura', value: veri === undefined ? '—' : String(invoices.length) },
          ].map((stat) => (
            <div key={stat.label} className="flex items-center gap-2">
              <span className="text-[12px] text-[#9ca3af]">{stat.label}</span>
              <span className="num text-[13px] font-semibold text-[#111827]">{stat.value}</span>
            </div>
          ))
        )}
      </div>

      {/* içerik */}
      <div className="space-y-6 px-6 py-8">
        {orgId === null ? (
          <p data-testid="faturalama-org-yok" className="text-[13px] text-[#6b7280]">
            Faturalama bilgisi için önce bir organizasyon seçin.
          </p>
        ) : null}

        {billingHata !== null ? (
          <ErrorState
            title="Abonelik bilgisi alınamadı"
            description={billingHata}
            onRetry={() => void billing.refetch()}
          />
        ) : null}
        {ucYok ? (
          <p className="text-[12px] text-[#9ca3af]">
            Bu sunucu sürümü faturalama ucunu sunmuyor (§45 henüz yayında değil).
          </p>
        ) : null}

        {/* ---------------------------- abonelik ---------------------------- */}
        <Card
          title={
            <span className="flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-[#9ca3af]" aria-hidden="true" />
              Abonelik
            </span>
          }
          subtitle="Durum yalnız ödeme sağlayıcısının bildirimiyle değişir (§45.1); iptal DAİMA dönem sonunda yürürlüğe girer (§45.3)."
          actions={
            durum === null ? null : (
              // Rozet bir `span`a sarılır: `Badge` kendi prop kümesini taşır (data-* geçmez).
              <span data-testid="abonelik-durumu">
                <Badge tone={durum.tone} dot title={durum.title}>
                  {durum.label}
                </Badge>
              </span>
            )
          }
        >
          {subscription === null ? (
            <p data-testid="abonelik-yok" className="text-[13px] text-[#374151]">
              Etkin abonelik yok — organizasyonunuz <span className="font-medium">ücretsiz plan</span>da.
              Aşağıdaki planlardan birini seçerek yükseltebilirsiniz; ücretsiz planda veri
              toplanmaya devam eder, yalnız aylık olay limiti düşüktür (§44.1).
            </p>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-[13px] text-[#374151]">
                <span>
                  Plan: <span className="font-medium">{mevcutPlan?.name ?? subscription.planId}</span>
                </span>
                <span>
                  {/* Sağlayıcı dönem sonunu bildirmediyse '—' — tarih UYDURULMAZ (§45.2). */}
                  Dönem sonu:{' '}
                  <span className="num" data-testid="abonelik-donem-sonu">
                    {subscription.currentPeriodEnd === null
                      ? '—'
                      : formatDate(subscription.currentPeriodEnd)}
                  </span>
                </span>
                <span className="text-[#9ca3af]">Sağlayıcı: {subscription.provider}</span>
              </div>

              {subscription.status === 'past_due' ? (
                <p className="rounded-xl border border-[#fde68a] bg-[#fffbeb] px-4 py-2.5 text-[12px] text-[#92400e]">
                  Son ödeme alınamadı. Planınız <span className="font-medium">değişmedi</span> ve
                  hiçbir özellik kısılmadı (§45.3); ödeme yönteminizi güncelleyip yeniden deneyin.
                </p>
              ) : null}

              {subscription.status !== 'canceled' ? (
                <button
                  type="button"
                  data-testid="abonelik-iptal"
                  className="btn-ghost"
                  disabled={!yetkili || cancel.isPending}
                  title={
                    yetkili
                      ? 'Abonelik dönem sonunda sona erer; o güne kadar hizmet sürer.'
                      : 'Bu işlem organizasyon yöneticisi (admin) yetkisi ister.'
                  }
                  onClick={() => cancel.mutate()}
                >
                  {cancel.isPending ? 'İletiliyor…' : 'Aboneliği iptal et'}
                </button>
              ) : null}

              {cancel.isSuccess ? (
                <p className="text-[12px] text-[#6b7280]">
                  İptal talebi iletildi.{' '}
                  {cancel.data.effectiveAt === null
                    ? 'Abonelik dönem sonunda sona erecek.'
                    : `Abonelik ${formatDate(cancel.data.effectiveAt)} tarihinde sona erecek.`}{' '}
                  O güne kadar planınız aynen sürer.
                </p>
              ) : null}
            </div>
          )}

          {islemHata !== null ? (
            <p role="alert" data-testid="faturalama-hata" className="mt-3 text-[12px] text-red-600">
              {islemHata}
            </p>
          ) : null}

          {!yetkili ? (
            <p data-testid="yetki-uyarisi" className="mt-3 text-[12px] text-[#6b7280]">
              Bu sayfayı salt-okunur görüyorsunuz: plan değiştirmek ve aboneliği iptal etmek
              organizasyon yöneticisinin (admin) yetkisindedir.
            </p>
          ) : null}
        </Card>

        {/* ----------------------------- planlar ----------------------------- */}
        <Card
          title="Planlar"
          subtitle="Plan yükseltmek de düşürmek de yeni bir ödeme akışıdır (§45.3); ücretsiz plana dönüş ise iptalle olur."
        >
          {plans.isError ? (
            <p className="text-[12px] text-red-600">{(plans.error as Error).message}</p>
          ) : plans.isLoading ? (
            <p className="animate-pulse text-[12px] text-[#9ca3af]">Plan kataloğu yükleniyor…</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="plan-kartlari">
              {planListesi.map((plan) => {
                const aktif = mevcutPlanId === plan.id;
                const ucretsiz = plan.priceKurus <= 0;
                // Etiket dürüst olsun: daha ucuz bir plan "yükseltme" değildir.
                const yukseltme = mevcutPlan === null || plan.priceKurus > mevcutPlan.priceKurus;
                return (
                  <div
                    key={plan.id}
                    data-testid="plan-karti"
                    data-plan={plan.id}
                    data-mevcut={aktif ? '1' : '0'}
                    className={`flex flex-col gap-3 rounded-xl border p-4 ${
                      aktif ? 'border-brand bg-[#fff4ec]' : 'border-[#e5e7eb] bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[14px] font-semibold text-[#111827]">{plan.name}</p>
                        <p className="num mt-0.5 text-[13px] text-[#374151]">
                          {ucretsiz ? 'Ücretsiz' : `${formatKurus(plan.priceKurus, plan.currency)} / ay`}
                        </p>
                      </div>
                      {aktif ? (
                        <Badge tone="accent" dot title="Organizasyonunuzun yürürlükteki planı">
                          mevcut plan
                        </Badge>
                      ) : null}
                    </div>

                    <p className="text-[12px] text-[#6b7280]">
                      <span className="num">{formatNumber(plan.monthlyEventLimit)}</span> olay/ay
                    </p>

                    {planOzellikleri(plan).length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {planOzellikleri(plan).map((ozellik) => (
                          <Badge key={ozellik} tone="outline" size="sm">
                            <Check className="h-3 w-3" aria-hidden="true" />
                            {ozellik}
                          </Badge>
                        ))}
                      </div>
                    ) : null}

                    <div className="mt-auto pt-1">
                      {aktif ? (
                        <p className="text-[12px] text-[#6b7280]">Bu plandasınız.</p>
                      ) : ucretsiz ? (
                        /*
                         * §45.1 — ücretsiz plana checkout AÇILMAZ (uç 400 `free_plan` döner):
                         * 0 TL'lik bir ödeme sağlayıcıda anlamsızdır. Doğru eylem iptaldir.
                         */
                        <p className="text-[12px] text-[#9ca3af]">
                          Ücretsiz plana dönmek için aboneliğinizi iptal edin.
                        </p>
                      ) : (
                        <button
                          type="button"
                          data-testid="plan-yukselt"
                          data-plan={plan.id}
                          className="btn-primary"
                          disabled={!yetkili || checkout.isPending || orgId === null}
                          title={
                            yetkili
                              ? 'Ödeme sayfasına yönlendirilirsiniz; abonelik ancak ödeme onaylanınca değişir.'
                              : 'Bu işlem organizasyon yöneticisi (admin) yetkisi ister.'
                          }
                          onClick={() => checkout.mutate(plan.id)}
                        >
                          {checkout.isPending && checkout.variables === plan.id
                            ? 'Yönlendiriliyor…'
                            : yukseltme
                              ? 'Planı yükselt'
                              : 'Bu plana geç'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* -------------------------- fatura geçmişi ------------------------- */}
        <Card
          title={
            <span className="flex items-center gap-2">
              <Receipt className="h-4 w-4 text-[#9ca3af]" aria-hidden="true" />
              Fatura geçmişi
            </span>
          }
          subtitle="Satırlar ödeme sağlayıcısının AYNASIDIR (§45.2): resmî belge/PDF sağlayıcının panelinden alınır."
        >
          <div data-testid="fatura-listesi">
            <Table
              rows={invoices}
              rowKey={(row) => row.id}
              loading={billing.isLoading}
              emptyTitle="Henüz fatura yok"
              emptyDescription="Ücretsiz planda fatura oluşmaz; ilk ödeme alındığında satırlar burada belirir."
              columns={[
                {
                  key: 'issued_at',
                  header: 'Tarih',
                  render: (row) => (
                    <span className="num text-[12px] text-[#374151]">{formatDate(row.issuedAt)}</span>
                  ),
                },
                {
                  key: 'amount',
                  header: 'Tutar',
                  align: 'right',
                  render: (row) => (
                    <span className="text-[#111827]">
                      {formatKurus(row.amountKurus, row.currency)}
                    </span>
                  ),
                },
                {
                  key: 'status',
                  header: 'Durum',
                  render: (row) => {
                    const rozet = faturaRozeti(row.status);
                    return (
                      <Badge tone={rozet.tone} dot>
                        {rozet.label}
                      </Badge>
                    );
                  },
                },
                {
                  key: 'provider_ref',
                  header: 'Sağlayıcı kaydı',
                  cellClassName: 'hidden md:table-cell',
                  render: (row) => (
                    // Destek konuşmalarında tek ortak kimlik budur; yoksa '—'.
                    <span className="font-mono text-[12px] text-[#9ca3af]">
                      {row.providerRef ?? '—'}
                    </span>
                  ),
                },
              ]}
            />
          </div>
        </Card>
      </div>
    </div>
  );
}
